import { FC, useMemo, useCallback, useState, forwardRef, useContext } from 'react';
import { AppBar, Box, Button, Chip, Dialog, Divider, IconButton, List, Paper, Slide, styled, Toolbar, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faCodeBranch, faEarthOceania, faInputText, faLinkHorizontal, faListOl, faObjectGroup, faSlash, faSquareList, faTableList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, IHasChildren, InputOnlyField, IValidationError, Text } from "@ngt/forms";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FieldType, IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata } from "@/api/dtos";
import { ConfigurationType, IConfigurationItem, ISourceDataConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";
import ConfigurationFormEditContext, { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { Condition, ConditionMode, useFormActions, useScopedField } from '@ngt/forms-core';
import { TransitionProps } from '@mui/material/transitions';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

export const ConfigurationIconBox = styled(Box)(({theme}) => ({
    fontSize: '1.5rem',
    marginRight: theme.spacing(2)
}));

export const ConfigurationDividerBox = styled(Box)(({theme}) => ({
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
}));

export const ConfigurationBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

export const ConfigurationTitle = styled(Typography)(({theme}) => ({
    margin: theme.spacing(2, 2, 2, 2)
}));

export const ConfigurationFlexBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    minHeight: 52
}));

export const ConfigurationInfoChip = styled(Chip)(({theme}) => ({
    marginLeft: 'auto',
}));


export const OtherVersionsBox = styled(Box)(({theme}) => ({
}));

export const OtherVersionsItem = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 2, 0, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

export interface IConfigurationFormEditInfoProps {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey?: string;
    formMetadata?: IFormMetadata;
    formVersionKey?: string;
    formVersionMetadata?: IFormVersionMetadata;
    formSectionKey?: string;
    formSectionMetadata?: IFormSectionMetadata;
    formFieldKey?: string;
    formFieldMetadata?: IFormFieldMetadata;
    formFieldTypeLabel?: string;
    formFieldValueKey?: string;
    formFieldValueMetadata?: IFormFieldValueMetadata;
    isGlobal: boolean;
    isTrial: boolean;
    dividers?: boolean;
}

const ConfigurationFormEditInfo: FC<IConfigurationFormEditInfoProps> = ({
    isGlobal,
    isTrial,
    eventKey,
    eventMetadata,
    formFieldKey,
    formFieldMetadata,
    formFieldTypeLabel,
    formFieldValueKey,
    formFieldValueMetadata,
    formKey,
    formMetadata,
    formSectionKey,
    formSectionMetadata,
    formVersionKey,
    formVersionMetadata,
    dividers
}) => {

    return (
        <>
            {
                (isTrial || isGlobal) && (
                    <>
                        <ConfigurationFlexBox>
                            {
                                isGlobal && (
                                    <Chip 
                                        label="Global Configuration"
                                        size="small"
                                        color="secondary"
                                    />
                                )
                            }
                            {
                                isTrial && (
                                    <Chip 
                                        label="Trial Configuration"
                                        size="small"
                                        color="secondary"
                                    />
                                )
                            }
                        </ConfigurationFlexBox>
                        {
                            dividers && (
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                            )
                        }
                    </>
                )
            }
            {
                eventKey && (
                    <ConfigurationFlexBox>
                        <ConfigurationIconBox>
                            <FontAwesomeIcon fixedWidth icon={faCalendarDays} />
                        </ConfigurationIconBox>
                        {eventMetadata?.name ?? 'Unknown Event'}
                        {
                            eventMetadata && eventMetadata.repeating && (
                                <ConfigurationInfoChip 
                                    label="Repeating"
                                    size="small"
                                    color="primary"
                                />
                            )
                        }
                    </ConfigurationFlexBox>
                )
            }
            {
                formKey && (
                    <>
                        {
                            eventKey && dividers && (
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                            )
                        }
                        <ConfigurationFlexBox>
                            <ConfigurationIconBox>
                                <FontAwesomeIcon fixedWidth icon={faSquareList} />
                            </ConfigurationIconBox>
                            {formMetadata?.name ?? 'Unknown Form'}
                        </ConfigurationFlexBox>
                        {
                            formVersionKey && (
                                <>
                                    {
                                        dividers && (
                                            <ConfigurationDividerBox>
                                                <Divider />
                                            </ConfigurationDividerBox>
                                        )
                                    }
                                    <ConfigurationFlexBox>
                                        <ConfigurationIconBox>
                                            <FontAwesomeIcon fixedWidth icon={faCodeBranch} />
                                        </ConfigurationIconBox>
                                        {formVersionMetadata?.name ?? 'Unknown Version'}
                                    </ConfigurationFlexBox>
                                    {
                                        formSectionKey && (
                                            <>
                                                {
                                                    dividers && (
                                                        <ConfigurationDividerBox>
                                                            <Divider />
                                                        </ConfigurationDividerBox>
                                                    )
                                                }
                                                <ConfigurationFlexBox>
                                                    <ConfigurationIconBox>
                                                        <FontAwesomeIcon fixedWidth icon={formSectionMetadata?.repeating ? faTableList : faObjectGroup} />
                                                    </ConfigurationIconBox>
                                                    {formSectionMetadata?.name ?? 'Unknown Section'}
                                                    <ConfigurationInfoChip 
                                                        label={formSectionMetadata?.repeating ? 'Collection' : 'Group'}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </ConfigurationFlexBox>
                                            </>
                                        )
                                    }
                                    {
                                        formFieldKey && (
                                            <>
                                                {
                                                    dividers && (
                                                        <ConfigurationDividerBox>
                                                            <Divider />
                                                        </ConfigurationDividerBox>
                                                    )
                                                }
                                                <ConfigurationFlexBox>
                                                    <ConfigurationIconBox>
                                                        <FontAwesomeIcon fixedWidth icon={faInputText} />
                                                    </ConfigurationIconBox>
                                                    {formFieldMetadata?.name ?? 'Unknown Field'}
                                                    <ConfigurationInfoChip 
                                                        label={formFieldTypeLabel}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </ConfigurationFlexBox>
                                                {
                                                    formFieldValueKey && (
                                                        <>
                                                            {
                                                                dividers && (
                                                                    <ConfigurationDividerBox>
                                                                        <Divider />
                                                                    </ConfigurationDividerBox>
                                                                )
                                                            }
                                                            <ConfigurationFlexBox>
                                                                <ConfigurationIconBox>
                                                                    <FontAwesomeIcon fixedWidth icon={faListOl} />
                                                                </ConfigurationIconBox>
                                                                {formFieldValueMetadata?.name ?? 'Unknown Value'}
                                                                <ConfigurationInfoChip 
                                                                    label={`ID: ${formFieldValueMetadata?.id}`}
                                                                    size="small"
                                                                    color="primary"
                                                                />
                                                            </ConfigurationFlexBox>
                                                        </>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                </>
                            )
                        }
                    </>
                )
            }
        </>
    )
}

export default ConfigurationFormEditInfo;